<template>
  <div class="export-view">
    <!-- 产品切换标签 -->
    <div class="product-tabs">
      <button
        v-for="(label, idx) in productLabels"
        :key="idx"
        class="product-tab"
        :class="{ active: currentProductIndex === idx }"
        @click="switchProduct(idx)"
      >
        {{ label }}
      </button>
      <button
        class="btn btn-secondary download-all-btn"
        :disabled="building"
        @click="onDownloadAll"
      >
        {{ building ? '打包中…' : '下载全部' }}
      </button>
    </div>

    <!-- ① 商品信息表单 -->
    <section class="card">
      <div class="card-head">
        <h3>{{ productLabels[currentProductIndex] }} 商品信息</h3>
        <span class="hint">基本信息用于 pack 的 product.json</span>
      </div>
      <div class="form-grid">
        <label class="fld">
          <span class="fld-label">SKU <i class="req">*</i></span>
          <input v-model="form.sku" placeholder="例如 TEST-SWEATER-001" />
        </label>
        <label class="fld">
          <span class="fld-label">店铺账号 <i class="req">*</i></span>
          <input
            v-model="form.storeAccount"
            list="store-account-list"
            placeholder="例如 cyzp"
            @blur="saveStoreAccountHistory"
          />
          <datalist id="store-account-list">
            <option v-for="s in storeAccountHistory" :key="s" :value="s" />
          </datalist>
        </label>
        <label class="fld">
          <span class="fld-label">商品类别 <i class="req">*</i></span>
          <select v-model="form.categoryKey">
            <option v-for="c in categories" :key="c.key" :value="c.key">{{ c.displayName }} → {{ c.productCategory }}</option>
          </select>
        </label>
        <label class="fld">
          <span class="fld-label">价格</span>
          <input v-model="form.price" placeholder="例如 39.9" />
        </label>
        <label class="fld">
          <span class="fld-label">库存数量</span>
          <input v-model="form.quantity" placeholder="例如 1000" />
        </label>
        <label class="fld">
          <span class="fld-label">品牌</span>
          <input
            v-model="form.brand"
            list="brand-list"
            placeholder="例如 VANTYL"
            @blur="saveBrandHistory"
          />
          <datalist id="brand-list">
            <option v-for="b in brandHistory" :key="b" :value="b" />
          </datalist>
        </label>
        <div class="fld">
          <span class="fld-label">售卖形式（自动检测）</span>
          <div class="readonly-value">
            <span class="tag" :class="isMultiVariant ? 'multi' : 'single'">{{ sellType }}</span>
            <span class="meta">{{ isMultiVariant ? `${colorCount} 个颜色` : '无颜色裂变' }}</span>
          </div>
        </div>
        <div class="fld fld-wide size-picker" v-if="isMultiVariant">
          <span class="fld-label">尺码列表</span>
          <div class="size-display" @click="sizePickerOpen = !sizePickerOpen">
            <div class="size-chips">
              <span v-for="size in selectedSizes" :key="size" class="size-chip" @click.stop>
                <span class="chip-text">{{ size }}</span>
                <button type="button" class="chip-remove" @click.stop="toggleSize(size)">×</button>
              </span>
              <span v-if="selectedSizes.length === 0" class="size-placeholder">点击选择尺码…</span>
            </div>
            <span class="caret" :class="{ open: sizePickerOpen }">▾</span>
          </div>
          <transition name="popup">
            <div v-if="sizePickerOpen" class="size-popup">
              <div class="size-search">
                <input v-model="sizeQuery" placeholder="搜索尺码…" />
              </div>
              <div class="size-actions">
                <button type="button" @click="selectAllVisible">全选</button>
                <button type="button" @click="clearAll">清空</button>
                <span class="count">{{ filteredSizes.length }} 项</span>
              </div>
              <div class="size-list">
                <label
                  v-for="size in filteredSizes"
                  :key="size"
                  class="size-option"
                  :class="{ checked: selectedSizes.includes(size) }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedSizes.includes(size)"
                    @change="toggleSize(size)"
                  />
                  <span class="opt-mark"></span>
                  <span class="opt-text">{{ size }}</span>
                </label>
                <div v-if="filteredSizes.length === 0" class="size-empty">无匹配尺码</div>
              </div>
            </div>
          </transition>
          <div v-if="sizePickerOpen" class="size-backdrop" @click="sizePickerOpen = false"></div>
        </div>
      </div>
    </section>

    <!-- ② 图片映射 -->
    <section class="card">
      <div class="card-head">
        <h3>图片映射</h3>
        <span class="hint">从 {{ productLabels[currentProductIndex] }} 工作台自动读取</span>
      </div>

      <div v-if="isMultiVariant" class="image-block">
        <div class="block-title">多变种图片</div>
        <div v-for="v in imageMapping.variants" :key="v.vi" class="variant-row">
          <div class="variant-info">
            <div class="variant-label">{{ v.colorLabel }}</div>
            <select
              class="color-select"
              :value="exp.variantColors[v.colorValue] || ''"
              @change="exp.variantColors[v.colorValue] = $event.target.value"
            >
              <option value="">选择颜色…</option>
              <option v-for="opt in exp.colorOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <span v-if="v.vi === 0" class="ai-hint">AI 自动填</span>
          </div>
          <div class="variant-thumbs">
            <div class="thumb main">
              <img v-if="v.main" :src="v.main" />
              <span class="thumb-name">main_{{ v.vi + 1 }}.png</span>
            </div>
            <div class="thumb swatch">
              <img v-if="v.swatch" :src="v.swatch" />
              <span class="thumb-name">swatch_{{ v.vi + 1 }}.png</span>
            </div>
            <div v-for="(url, ri) in v.sub" :key="ri" class="thumb">
              <img :src="url" />
              <span class="thumb-name">sub_{{ v.vi + 1 }}_{{ ri + 1 }}.png</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="image-block">
        <div class="block-title">单品图片</div>
        <div class="variant-row">
          <div class="variant-info">
            <div class="variant-label">主图</div>
          </div>
          <div class="variant-thumbs">
            <div v-for="(img, i) in imageMapping.single" :key="i" class="thumb">
              <img :src="img.url" />
              <span class="thumb-name">main_{{ i + 1 }}.png</span>
            </div>
            <div v-if="imageMapping.single.length === 0" class="empty-row">无宣传图，请先到图片生成页生成</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ③ 商品属性（AI 分析） -->
    <section class="card">
      <div class="card-head">
        <h3>商品属性</h3>
        <button class="btn btn-primary" :disabled="exp.analyzing" @click="exp.runAnalyze">
          {{ exp.analyzing ? '分析中…' : 'AI 分析属性' }}
        </button>
      </div>
      <p class="section-hint">
        AI 根据 {{ productLabels[currentProductIndex] }} 图片自动识别属性。有默认值的字段已预填（不计费字段），AI 只分析无默认值的字段。
      </p>
      <div v-if="exp.analyzeError" class="error-text">{{ exp.analyzeError }}</div>
      <div v-if="exp.aiDerivableFields.length === 0" class="empty-row">请先选择商品类别</div>
      <div v-else class="attr-grid">
        <div v-for="f in exp.aiDerivableFields" :key="f.key" class="attr-cell">
          <div class="attr-label">
            <span class="attr-name">{{ f.displayName }}</span>
            <span class="attr-type" :class="f.type">{{ f.type }}</span>
          </div>
          <div class="attr-value">
            <template v-if="Array.isArray(exp.analyzedFields[f.key])">
              <span v-if="exp.analyzedFields[f.key].length === 0" class="empty-val">—</span>
              <span v-for="v in exp.analyzedFields[f.key]" :key="v" class="value-chip">{{ v }}</span>
            </template>
            <template v-else>
              <span v-if="!exp.analyzedFields[f.key]" class="empty-val">—</span>
              <span v-else>{{ exp.analyzedFields[f.key] }}</span>
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- ④ 预览与下载 -->
    <section class="card">
      <div class="card-head">
        <h3>预览与下载</h3>
        <button class="btn btn-primary" :disabled="building" @click="onDownload">
          {{ building ? '打包中…' : '下载 pack ZIP' }}
        </button>
      </div>
      <details class="preview">
        <summary>查看 product.json 预览</summary>
        <pre>{{ previewJson }}</pre>
      </details>
      <div v-if="buildError" class="error-text">{{ buildError }}</div>
      <div class="tip">
        下载后将 ZIP 解压出的 <code>pack_{{ form.sku || 'SKU' }}/</code> 文件夹复制进店小秘
        <code>C:\Users\djjserver\Desktop\dianxiaomi\packs\</code>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useExportStore } from '@/stores/useExportStore'

const exp = useExportStore()
const {
  form, categories, sellType, isMultiVariant, colorCount,
  imageMapping, building, buildError,
  currentProductIndex, productLabels
} = storeToRefs(exp)
const { switchProduct } = exp

const previewJson = computed(() => {
  try { return JSON.stringify(exp.buildProductJson(), null, 2) } catch (e) { return '// ' + e.message }
})

const availableSizes = [
  '0X', '1X', '2X', '3X', '4X', '5X', '6X', '7X', '8X', '9X', '10X',
  '3X-Small(3-小)', '4X-Small(4-小)', '5X-Small(5-小)', '6X-Small(6-小)',
  'XX-Small(XX-小)', 'X-Small(X-小)',
  'Small(小)', 'Medium(中等)', 'Large(大)',
  'X-Large(X-大号)', 'XX-Large(XX-大号)',
  '3X-Large(3-大)', '4X-Large(4-大)', '5X-Large(5-大)', '6X-Large(6-大)',
  '7X-Large(7-大)', '8X-Large(8-大号)', '9X-Large(9x大)', '10X-Large(10-大)',
  'One Size(一个尺寸)'
]
const sizePickerOpen = ref(false)
const sizeQuery = ref('')
const selectedSizes = computed(() =>
  (exp.form.sizeList || '').split(/[,，]/).map(s => s.trim()).filter(Boolean)
)
const filteredSizes = computed(() => {
  const q = sizeQuery.value.trim().toLowerCase()
  if (!q) return availableSizes
  return availableSizes.filter(s => s.toLowerCase().includes(q))
})
const toggleSize = (size) => {
  const cur = [...selectedSizes.value]
  const idx = cur.indexOf(size)
  if (idx >= 0) cur.splice(idx, 1)
  else cur.push(size)
  exp.form.sizeList = cur.join(', ')
}
const selectAllVisible = () => {
  const cur = new Set(selectedSizes.value)
  filteredSizes.value.forEach(s => cur.add(s))
  exp.form.sizeList = [...cur].join(', ')
}
const clearAll = () => {
  exp.form.sizeList = ''
  sizeQuery.value = ''
}

const onDownload = async () => {
  try { await exp.downloadPack() } catch (e) { console.error(e) }
}

const onDownloadAll = async () => {
  try { await exp.downloadAllPacks() } catch (e) { console.error(e) }
}

// 品牌 / 店铺账号 历史记录持久化到 localStorage
const BRAND_KEY = 'zhipan.export.brandHistory'
const STORE_KEY = 'zhipan.export.storeAccountHistory'

const loadHistory = (key) => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}
const persistHistory = (key, value, list) => {
  const v = (value || '').trim()
  if (!v || list.value.includes(v)) return
  list.value = [v, ...list.value].slice(0, 20)
  localStorage.setItem(key, JSON.stringify(list.value))
}

const brandHistory = ref(loadHistory(BRAND_KEY))
const storeAccountHistory = ref(loadHistory(STORE_KEY))
const saveBrandHistory = () => persistHistory(BRAND_KEY, exp.form.brand, brandHistory)
const saveStoreAccountHistory = () => persistHistory(STORE_KEY, exp.form.storeAccount, storeAccountHistory)

onMounted(() => {
  exp.loadCategories()
  // 默认值也存入历史，方便下次直接选
  if (exp.form.storeAccount) saveStoreAccountHistory()
})
</script>

<style scoped>
.export-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: -28px;
}
.product-tabs {
  display: flex;
  gap: 8px;
  align-items: center;
}
.product-tab {
  padding: 6px 18px;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  background: var(--bg-white);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
  transition: all var(--dur-fast) var(--ease-out);
}
.product-tab:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}
.product-tab.active {
  background: var(--primary-color);
  color: #fff;
  border-color: var(--primary-color);
}
.download-all-btn {
  margin-left: auto;
}
.btn-secondary {
  background: var(--bg-white);
  color: var(--primary-active);
  border: 1px solid var(--primary-color);
}
.btn-secondary:hover:not(:disabled) {
  background: var(--primary-soft);
}
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.card {
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: var(--shadow-sm);
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.card-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-title);
}
.card-head .hint {
  font-size: 12px;
  color: var(--text-sub);
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 16px;
}
.fld {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fld-wide { grid-column: span 2; }
.fld-label {
  font-size: 12px;
  color: var(--text-sub);
  font-weight: 500;
}
.req { color: var(--danger); font-style: normal; }
.fld input, .fld select {
  padding: 6px 10px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  font-size: 13.5px;
  background: var(--bg-white);
  color: var(--text-title);
  outline: none;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.fld input:focus, .fld select:focus {
  border-color: var(--primary-color);
}
.readonly-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}
.size-picker { position: relative; }
.size-display {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-height: 34px;
  padding: 4px 8px 4px 6px;
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  background: var(--bg-white);
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.size-display:hover { border-color: var(--primary-color); }
.size-display:focus-within { border-color: var(--primary-color); box-shadow: 0 0 0 3px var(--primary-soft); }
.size-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  align-items: center;
}
.size-placeholder {
  color: var(--text-sub);
  font-size: 13.5px;
  padding: 2px 4px;
}
.size-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 8px;
  background: var(--primary-soft);
  color: var(--primary-active);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  animation: chip-in 0.18s var(--ease-out);
}
@keyframes chip-in {
  from { transform: scale(0.8); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
.chip-remove {
  border: none;
  background: transparent;
  color: var(--primary-active);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background var(--dur-fast);
}
.chip-remove:hover { background: rgba(0, 0, 0, 0.08); }
.caret {
  color: var(--text-sub);
  font-size: 11px;
  transition: transform var(--dur-fast) var(--ease-out);
  align-self: center;
  padding: 0 4px;
}
.caret.open { transform: rotate(180deg); }

.size-popup {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--bg-white);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.06);
  z-index: 10;
  overflow: hidden;
}
.size-search {
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--border-color);
}
.size-search input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  font-size: 13px;
  background: var(--bg-white);
  color: var(--text-title);
  outline: none;
  transition: border-color var(--dur-fast);
}
.size-search input:focus { border-color: var(--primary-color); }
.size-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--surface-soft);
  border-bottom: 1px solid var(--border-color);
}
.size-actions button {
  border: none;
  background: transparent;
  color: var(--primary-active);
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background var(--dur-fast);
}
.size-actions button:hover { background: var(--primary-soft); }
.size-actions .count {
  margin-left: auto;
  font-size: 11px;
  color: var(--text-sub);
}
.size-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 2px;
  padding: 6px;
  max-height: 240px;
  overflow-y: auto;
}
.size-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 12.5px;
  color: var(--text-body);
  cursor: pointer;
  transition: background var(--dur-fast);
  position: relative;
  user-select: none;
}
.size-option:hover { background: var(--surface-soft); }
.size-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.opt-mark {
  width: 14px;
  height: 14px;
  border: 1.5px solid var(--border-strong);
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all var(--dur-fast) var(--ease-out);
}
.size-option.checked .opt-mark {
  background: var(--primary-color);
  border-color: var(--primary-color);
}
.size-option.checked .opt-mark::after {
  content: '';
  width: 7px;
  height: 4px;
  border-left: 1.5px solid #fff;
  border-bottom: 1.5px solid #fff;
  transform: rotate(-45deg) translate(0, -1px);
}
.size-option.checked { color: var(--primary-active); font-weight: 500; }
.size-empty {
  grid-column: 1 / -1;
  padding: 16px;
  text-align: center;
  color: var(--text-sub);
  font-size: 12px;
}

.popup-enter-active, .popup-leave-active {
  transition: opacity 0.16s var(--ease-out), transform 0.16s var(--ease-out);
  transform-origin: top center;
}
.popup-enter-from, .popup-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-4px);
}

.size-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 5;
}
.tag {
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}
.tag.multi { background: var(--primary-soft); color: var(--primary-active); }
.tag.single { background: var(--surface-soft); color: var(--text-body); }
.meta { font-size: 12px; color: var(--text-sub); }

.empty-row {
  padding: 16px;
  text-align: center;
  color: var(--text-sub);
  font-size: 13px;
}

.image-block, .copy-block {
  margin-top: 8px;
}
.block-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-title);
  margin-bottom: 8px;
}
.variant-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--border-color);
}
.variant-row:last-child { border-bottom: none; }
.variant-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
  padding-top: 4px;
}
.variant-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
}
.color-select {
  padding: 4px 8px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  font-size: 12.5px;
  background: var(--bg-white);
  color: var(--text-title);
  outline: none;
  cursor: pointer;
  transition: border-color var(--dur-fast);
}
.color-select:focus { border-color: var(--primary-color); }
.ai-hint {
  font-size: 10px;
  color: var(--primary-active);
  background: var(--primary-soft);
  padding: 1px 6px;
  border-radius: 8px;
  align-self: flex-start;
}
.variant-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.thumb {
  position: relative;
  width: 70px;
}
.thumb img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  display: block;
}
.thumb.main img { border-color: var(--primary-color); }
.thumb.swatch img { border-color: var(--warning, #f59e0b); }
.thumb-name {
  display: block;
  font-size: 10px;
  color: var(--text-sub);
  margin-top: 2px;
  text-align: center;
  font-family: monospace;
}

.btn {
  padding: 7px 18px;
  border-radius: var(--radius-sm);
  border: none;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.btn-primary {
  background: var(--primary-gradient);
  color: #fff;
  box-shadow: var(--shadow-primary);
}
.btn-primary:hover:not(:disabled) {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview {
  margin-top: 8px;
  background: var(--surface-soft);
  border-radius: 6px;
  padding: 8px 12px;
}
.preview summary {
  cursor: pointer;
  font-size: 13px;
  color: var(--text-body);
  font-weight: 500;
}
.preview pre {
  margin: 8px 0 0;
  font-size: 11.5px;
  font-family: monospace;
  color: var(--text-title);
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 320px;
  overflow-y: auto;
}

.error-text {
  margin-top: 8px;
  padding: 6px 10px;
  background: var(--danger-soft);
  color: var(--danger);
  border-radius: 4px;
  font-size: 12.5px;
}

.tip {
  margin-top: 10px;
  padding: 8px 12px;
  background: var(--primary-softer);
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-body);
  line-height: 1.6;
}
.tip code {
  background: var(--bg-white);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11.5px;
  color: var(--text-title);
}

.section-hint {
  margin: -4px 0 12px;
  font-size: 12px;
  color: var(--text-sub);
  line-height: 1.5;
}

.attr-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 16px;
}
.attr-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  background: var(--surface-soft);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}
.attr-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.attr-name {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-title);
}
.attr-type {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  background: var(--bg-white);
  color: var(--text-sub);
  font-family: monospace;
  border: 1px solid var(--border-color);
}
.attr-type.multi_checkbox { color: var(--primary-active); border-color: var(--primary-color); }
.attr-type.select, .attr-type.radio { color: var(--warning, #b45309); border-color: var(--warning, #f59e0b); }
.attr-value {
  font-size: 12.5px;
  color: var(--text-body);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-height: 18px;
}
.value-chip {
  display: inline-flex;
  padding: 2px 8px;
  background: var(--primary-soft);
  color: var(--primary-active);
  border-radius: 10px;
  font-size: 11.5px;
  font-weight: 500;
}
.empty-val {
  color: var(--text-sub);
  font-size: 12px;
}

@media (max-width: 720px) {
  .form-grid { grid-template-columns: 1fr; }
  .fld-wide { grid-column: span 1; }
  .attr-grid { grid-template-columns: 1fr; }
}
</style>
